/*
 * Copyright (c) 2023. Collabro Limited
 */
import React, { useContext, useRef, useState } from "react";
import { Badge, Collapse, IconButton, Slide, TextField } from "@mui/material";
import { FilterAlt, Settings, Sort } from "@mui/icons-material";
import FilterScreen from "../Filter/FilterScreen";
import { AuthenticatedContext } from "../Authenticated/Authenticated";
import { FilterProps } from "../Filter/FilterInput";
import SortScreen from "../Sort/SortScreen";
import SettingsScreen from "../Settings/SettingsScreen";
import { DisplayController } from "RosterData";
import SearchIcon from "@mui/icons-material/Search";
import QuickSearchCollapse from "../QuickSearch/QuickSearchCollapse";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import LayersScreen from "../Layers/LayersScreen";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import WrappedOptions from "./WrappedOptions";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import BookingsScreen from "../Bookings/BookingsScreen";

export interface PopoutScreenProps {
  anchorEl: HTMLButtonElement | null;
  initialData: FilterProps[] | any;
  getLatestReportData: any;
  setInitialData: any;
  onClose: any;
  handleError: any;
}

interface OptionsCellProps {
  width: number;
}

const Options = (props: OptionsCellProps) => {
  const {
    initialFilters,
    setInitialFilters,
    displayController,
    setDisplayController,
    initialSorting,
    setInitialSorting,
    getLatestReportData,
    handleError,
    searchQuery,
    initialLayers,
  } = useContext(AuthenticatedContext);
  const [filterOpen, setFilterOpen] = useState(null);
  const [sortOpen, setSortOpen] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(null);
  const [quickSearchOpen, setQuickSearchOpen] = useState(null);
  const [layersOpen, setLayersOpen] = useState(null);
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(null);
  const [bookingsOpen, setBookingsOpen] = useState(null);
  const handleFilterOpen = (e: any) => {
    setFilterOpen(e.currentTarget);
  };

  const handleSortOpen = (e: any) => {
    setSortOpen(e.currentTarget);
  };

  const handleSettingsOpen = (e: any) => {
    setSettingsOpen(e.currentTarget);
  };

  const handleQuickSearchOpen = (e: any): void => {
    if (quickSearchOpen) {
      setQuickSearchOpen(null);
    } else {
      setQuickSearchOpen(e.currentTarget);
    }
  };

  const countTrueProperties = (obj: DisplayController): number => {
    return Object.values(obj).reduce(
      (count, value) => count + (value ? 1 : 0),
      0
    );
  };

  const handleLayersScreenOpen = (e: any): void => {
    setLayersOpen(e.currentTarget);
  };

  const handleMoreOptionsOpen = (e: any): void => {
    setMoreOptionsOpen(e.currentTarget);
  };

  const handleBookingsOpen = (e: any): void => {
    setBookingsOpen(e.currentTarget);
  };

  return (
    <>
      <IconButton color="primary" onClick={handleBookingsOpen}>
        <AddCircleIcon />
      </IconButton>
      <IconButton color="primary" onClick={handleFilterOpen}>
        <Badge badgeContent={initialFilters.length} color="secondary">
          <FilterAlt />
        </Badge>
      </IconButton>
      <IconButton color="primary" onClick={handleSortOpen}>
        <Badge
          variant="dot"
          invisible={initialSorting.length === 0}
          color="secondary"
        >
          <Sort />
        </Badge>
      </IconButton>
      <IconButton color="primary" onClick={handleSettingsOpen}>
        <Badge
          badgeContent={countTrueProperties(displayController!)}
          color="secondary"
        >
          <Settings />
        </Badge>
      </IconButton>
      {props.width < 400 ? (
        <IconButton color="primary" onClick={handleMoreOptionsOpen}>
          <Badge
            variant="dot"
            invisible={
              searchQuery === "" &&
              (!initialLayers || initialLayers.length === 0)
            }
            color="secondary"
          >
            <MoreHorizIcon />
          </Badge>
        </IconButton>
      ) : (
        <>
          {/*If the Options Cell is big enough expand the rest*/}
          <IconButton color="primary" onClick={handleLayersScreenOpen}>
            <Badge badgeContent={initialLayers.length} color="secondary">
              <AutoAwesomeMotionIcon />
            </Badge>
          </IconButton>
          <IconButton color="primary" onClick={handleQuickSearchOpen}>
            <Badge
              variant="dot"
              invisible={searchQuery === ""}
              color="secondary"
            >
              <SearchIcon />
            </Badge>
          </IconButton>
          <QuickSearchCollapse
            quickSearchOpen={quickSearchOpen}
            onClose={setQuickSearchOpen}
          />
        </>
      )}
      {bookingsOpen && (
        <BookingsScreen
          anchorEl={bookingsOpen}
          initialData={null}
          getLatestReportData={getLatestReportData}
          setInitialData={null}
          onClose={setBookingsOpen}
          handleError={handleError}
        />
      )}
      {filterOpen && (
        <FilterScreen
          anchorEl={filterOpen}
          onClose={setFilterOpen}
          initialData={initialFilters}
          setInitialData={setInitialFilters}
          getLatestReportData={getLatestReportData}
          handleError={handleError}
        />
      )}
      {sortOpen && (
        <SortScreen
          anchorEl={sortOpen}
          initialData={initialSorting}
          getLatestReportData={getLatestReportData}
          setInitialData={setInitialSorting}
          onClose={setSortOpen}
          handleError={handleError}
        />
      )}
      {settingsOpen && (
        <SettingsScreen
          anchorEl={settingsOpen}
          initialData={displayController}
          getLatestReportData={getLatestReportData}
          setInitialData={setDisplayController}
          onClose={setSettingsOpen}
          handleError={handleError}
        />
      )}
      {layersOpen && (
        <LayersScreen
          anchorEl={layersOpen}
          initialData={null}
          getLatestReportData={getLatestReportData}
          setInitialData={null}
          onClose={setLayersOpen}
          handleError={handleError}
        />
      )}
      {moreOptionsOpen && (
        <WrappedOptions
          anchorEl={moreOptionsOpen}
          initialData={null}
          getLatestReportData={getLatestReportData}
          setInitialData={null}
          onClose={setMoreOptionsOpen}
          handleError={handleError}
        />
      )}
    </>
  );
};

export default Options;
